import React, { useMemo, useState } from "react";
import {
  Box,
  TableRow,
  TableCell,
  Typography,
  Button,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

const tdLabelSx = {
  width: 380,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 700,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1.5, verticalAlign: "middle" };

const noteSx = {
  color: "#666",
  fontSize: 12,
  lineHeight: 1.35,
  mt: 0.5,
  fontWeight: 500,
};

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#fff",
  p: { xs: 2, sm: 3 },
  borderRadius: 2,
  width: "min(760px, calc(100vw - 32px))",
  boxShadow: 24,
  outline: "none",
};

const tipoDocumentoOptions = [
  { value: "01", label: "Factura", shortLabel: "Factura" },
  { value: "04", label: "Nota de Crédito", shortLabel: "N. Crédito" },
  { value: "05", label: "Nota de Débito", shortLabel: "N. Débito" },
  { value: "06", label: "Guía de Remisión", shortLabel: "Guía" },
  { value: "07", label: "Retención", shortLabel: "Retención" },
];

const tipoDocumentoMap = {
  "01": { label: "Factura", shortLabel: "Factura" },
  "04": { label: "Nota de Crédito", shortLabel: "N. Crédito" },
  "05": { label: "Nota de Débito", shortLabel: "N. Débito" },
  "06": { label: "Guía de Remisión", shortLabel: "Guía" },
  "07": { label: "Retención", shortLabel: "Retención" },
};

const normalizeTipoDocumento = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (v === "FACTURA" || v === "01") return "01";
  if (v === "NOTA_CREDITO" || v === "NOTA DE CREDITO" || v === "NOTA DE CRÉDITO" || v === "04") return "04";
  if (v === "NOTA_DEBITO" || v === "NOTA DE DEBITO" || v === "NOTA DE DÉBITO" || v === "05") return "05";
  if (v === "GUIA_REMISION" || v === "GUÍA DE REMISIÓN" || v === "GUIA DE REMISION" || v === "06") return "06";
  if (v === "RETENCION" || v === "RETENCIÓN" || v === "07") return "07";
  return v;
};

const toInt = (v) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : 0;
};

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("access") || "";
  const headers = { ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const normalizeCode = (value) => {
  const only = String(value || "").replace(/\D/g, "").slice(0, 3);
  return only ? only.padStart(3, "0") : "";
};

const formatSecuencial = (value) => {
  return String(toInt(value) || 1).padStart(9, "0");
};

const getTipoLabel = (tipo) => {
  const key = normalizeTipoDocumento(tipo);
  return tipoDocumentoMap[key]?.label || key;
};

const getTipoShortLabel = (tipo) => {
  const key = normalizeTipoDocumento(tipo);
  return tipoDocumentoMap[key]?.shortLabel || key;
};

export default function EmissionAndSequenceSettings({
  establecimientos = [],
  puntos = [],
  secuenciales = [],
  onRefresh,
  getCSRF,
  setSuccessMessage,
  setErrorMessage,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [puntoOpen, setPuntoOpen] = useState(false);
  const [puntoForm, setPuntoForm] = useState({
    establecimiento: establecimientos?.[0]?.codigo || "001",
    punto_emision: "",
  });

  const [secuencialOpen, setSecuencialOpen] = useState(false);
  const [secForm, setSecForm] = useState({
    tipo_documento: "01",
    establecimiento: establecimientos?.[0]?.codigo || "001",
    punto_emision: "",
    secuencia_inicial: "",
  });

  const establecimientosUnicos = useMemo(() => {
    const map = new Map();

    (establecimientos || []).forEach((e) => {
      const codigo = normalizeCode(e.codigo);
      if (!codigo) return;

      map.set(codigo, {
        codigo,
        nombre: e.nombre || codigo,
      });
    });

    (puntos || []).forEach((p) => {
      const codigo = normalizeCode(p.establecimiento);
      if (!codigo) return;

      if (!map.has(codigo)) {
        map.set(codigo, {
          codigo,
          nombre: p.establecimiento_nombre || codigo,
        });
      }
    });

    (secuenciales || []).forEach((s) => {
      const codigo = normalizeCode(s.establecimiento);
      if (!codigo) return;

      if (!map.has(codigo)) {
        map.set(codigo, {
          codigo,
          nombre: codigo,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.codigo.localeCompare(b.codigo));
  }, [establecimientos, puntos, secuenciales]);

  const puntosUnicos = useMemo(() => {
    const seen = new Set();

    return (puntos || [])
      .map((p) => ({
        establecimiento: normalizeCode(p.establecimiento),
        establecimiento_nombre: p.establecimiento_nombre || "",
        punto_emision: normalizeCode(p.punto_emision),
      }))
      .filter((p) => {
        if (!p.establecimiento || !p.punto_emision) return false;

        const key = `${p.establecimiento}-${p.punto_emision}`;
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      })
      .sort((a, b) => {
        if (a.establecimiento !== b.establecimiento) {
          return a.establecimiento.localeCompare(b.establecimiento);
        }

        return a.punto_emision.localeCompare(b.punto_emision);
      });
  }, [puntos]);

  const puntosAgrupados = useMemo(() => {
    const map = new Map();

    puntosUnicos.forEach((p) => {
      if (!map.has(p.establecimiento)) {
        const est = establecimientosUnicos.find((e) => e.codigo === p.establecimiento);

        map.set(p.establecimiento, {
          establecimiento: p.establecimiento,
          nombre: est?.nombre || p.establecimiento_nombre || p.establecimiento,
          puntos: [],
        });
      }

      map.get(p.establecimiento).puntos.push(p);
    });

    return Array.from(map.values()).sort((a, b) =>
      a.establecimiento.localeCompare(b.establecimiento)
    );
  }, [puntosUnicos, establecimientosUnicos]);

  const secuencialesUnicos = useMemo(() => {
    const map = new Map();

    for (const s of secuenciales || []) {
      const tipo = normalizeTipoDocumento(s.tipo_documento);
      const establecimiento = normalizeCode(s.establecimiento);
      const puntoEmision = normalizeCode(s.punto_emision);
      const secuencia = toInt(
        s.secuencia_inicial ?? s.secuencial_actual ?? s.secuencia_actual ?? 1
      );

      if (!tipo || !establecimiento || !puntoEmision) continue;

      const key = `${tipo}-${establecimiento}-${puntoEmision}`;
      const actual = map.get(key);

      if (!actual || secuencia >= toInt(actual.secuencia_inicial)) {
        map.set(key, {
          ...s,
          tipo_documento: tipo,
          establecimiento,
          punto_emision: puntoEmision,
          secuencia_inicial: secuencia,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      const ea = String(a.establecimiento || "");
      const eb = String(b.establecimiento || "");
      const pa = String(a.punto_emision || "");
      const pb = String(b.punto_emision || "");
      const ta = String(a.tipo_documento || "");
      const tb = String(b.tipo_documento || "");

      if (ea !== eb) return ea.localeCompare(eb);
      if (pa !== pb) return pa.localeCompare(pb);
      return ta.localeCompare(tb);
    });
  }, [secuenciales]);

  const secuencialesAgrupados = useMemo(() => {
    const map = new Map();

    secuencialesUnicos.forEach((s) => {
      const key = `${s.establecimiento}-${s.punto_emision}`;

      if (!map.has(key)) {
        const est = establecimientosUnicos.find((e) => e.codigo === s.establecimiento);

        map.set(key, {
          key,
          establecimiento: s.establecimiento,
          establecimiento_nombre: est?.nombre || s.establecimiento,
          punto_emision: s.punto_emision,
          secuenciales: [],
        });
      }

      map.get(key).secuenciales.push(s);
    });

    return Array.from(map.values()).sort((a, b) => {
      if (a.establecimiento !== b.establecimiento) {
        return a.establecimiento.localeCompare(b.establecimiento);
      }

      return a.punto_emision.localeCompare(b.punto_emision);
    });
  }, [secuencialesUnicos, establecimientosUnicos]);

  const puntosDelEstablecimiento = useMemo(() => {
    return puntosUnicos
      .filter((p) => p.establecimiento === secForm.establecimiento)
      .sort((a, b) => a.punto_emision.localeCompare(b.punto_emision));
  }, [puntosUnicos, secForm.establecimiento]);

  const getSecuencialActual = (tipoDocumento, establecimiento, puntoEmision) => {
    const tipo = normalizeTipoDocumento(tipoDocumento);
    const est = normalizeCode(establecimiento);
    const pto = normalizeCode(puntoEmision);

    const match = secuencialesUnicos.find(
      (s) =>
        normalizeTipoDocumento(s.tipo_documento) === tipo &&
        normalizeCode(s.establecimiento) === est &&
        normalizeCode(s.punto_emision) === pto
    );

    return match ? formatSecuencial(match.secuencia_inicial) : "000000001";
  };

  const openPuntoModal = () => {
    setPuntoForm({
      establecimiento: establecimientosUnicos[0]?.codigo || "001",
      punto_emision: "",
    });
    setPuntoOpen(true);
  };

  const openSecuencialModal = (item = null) => {
    if (item) {
      setSecForm({
        tipo_documento: normalizeTipoDocumento(item.tipo_documento),
        establecimiento: normalizeCode(item.establecimiento),
        punto_emision: normalizeCode(item.punto_emision),
        secuencia_inicial: formatSecuencial(item.secuencia_inicial),
      });

      setSecuencialOpen(true);
      return;
    }

    const primerPunto = puntosUnicos[0];
    const establecimiento =
      primerPunto?.establecimiento ||
      establecimientosUnicos[0]?.codigo ||
      "001";

    const puntoEmision =
      puntosUnicos.find((p) => p.establecimiento === establecimiento)?.punto_emision || "";

    const tipoDocumento = "01";

    setSecForm({
      tipo_documento: tipoDocumento,
      establecimiento,
      punto_emision: puntoEmision,
      secuencia_inicial: getSecuencialActual(tipoDocumento, establecimiento, puntoEmision),
    });

    setSecuencialOpen(true);
  };

  const addPunto = async () => {
    try {
      const establecimiento = normalizeCode(puntoForm.establecimiento);
      const punto = normalizeCode(puntoForm.punto_emision);

      if (!establecimiento) {
        setErrorMessage("Seleccione un establecimiento");
        return;
      }

      if (!punto || punto === "000") {
        setErrorMessage("Ingrese un punto de emisión válido");
        return;
      }

      const fd = new FormData();
      fd.append("establecimiento", establecimiento);
      fd.append("punto_emision", punto);

      const r = await fetch("/facturas/puntos/agregar/", {
        method: "POST",
        headers: getAuthHeaders({ "X-CSRFToken": getCSRF() }),
        body: fd,
      });

      const j = await r.json().catch(() => ({}));

      if (r.ok) {
        setSuccessMessage("Punto agregado");
        setPuntoOpen(false);
        setPuntoForm({
          establecimiento,
          punto_emision: "",
        });
        await onRefresh();
      } else {
        setErrorMessage(j.error || "No se pudo agregar");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const deletePunto = async (item) => {
    try {
      const r = await fetch("/facturas/puntos/eliminar/", {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        }),
        body: JSON.stringify({
          establecimiento: item.establecimiento,
          punto_emision: item.punto_emision,
        }),
      });

      const j = await r.json().catch(() => ({}));

      if (r.ok) {
        setSuccessMessage("Punto eliminado");
        await onRefresh();
      } else {
        setErrorMessage(j.error || "No se pudo eliminar");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const addSecuencial = async () => {
    try {
      if (!secForm.tipo_documento) {
        setErrorMessage("Seleccione un tipo de documento");
        return;
      }

      if (!secForm.establecimiento) {
        setErrorMessage("Seleccione un establecimiento");
        return;
      }

      if (!secForm.punto_emision) {
        setErrorMessage("Seleccione un punto de emisión");
        return;
      }

      const secuencia = parseInt(String(secForm.secuencia_inicial || "1").replace(/\D/g, ""), 10) || 1;

      const fd = new FormData();
      fd.append("tipo_documento", normalizeTipoDocumento(secForm.tipo_documento));
      fd.append("establecimiento", normalizeCode(secForm.establecimiento));
      fd.append("punto_emision", normalizeCode(secForm.punto_emision));
      fd.append("secuencia_inicial", String(secuencia));

      const r = await fetch("/facturas/secuenciales/actualizar/", {
        method: "POST",
        headers: getAuthHeaders({ "X-CSRFToken": getCSRF() }),
        body: fd,
      });

      const j = await r.json().catch(() => ({}));

      if (r.ok) {
        setSuccessMessage("Secuencia modificada");
        setSecuencialOpen(false);
        await onRefresh();
      } else {
        setErrorMessage(j.error || "No se pudo modificar");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const deleteSecuencial = async (item) => {
    try {
      const r = await fetch("/facturas/secuenciales/actualizar/", {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRF(),
        }),
        body: JSON.stringify({
          eliminar: 1,
          tipo_documento: normalizeTipoDocumento(item.tipo_documento),
          establecimiento: normalizeCode(item.establecimiento),
          punto_emision: normalizeCode(item.punto_emision),
        }),
      });

      const j = await r.json().catch(() => ({}));

      if (r.ok) {
        setSuccessMessage("Secuencia eliminada");
        await onRefresh();
      } else {
        setErrorMessage(j.error || "No se pudo eliminar");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const handleSecTipoChange = (value) => {
    setSecForm((p) => ({
      ...p,
      tipo_documento: value,
      secuencia_inicial: getSecuencialActual(value, p.establecimiento, p.punto_emision),
    }));
  };

  const handleSecEstablecimientoChange = (value) => {
    setSecForm((p) => {
      const nuevoEstablecimiento = normalizeCode(value);
      const primerPuntoDelEst =
        puntosUnicos.find((x) => x.establecimiento === nuevoEstablecimiento)?.punto_emision || "";

      return {
        ...p,
        establecimiento: nuevoEstablecimiento,
        punto_emision: primerPuntoDelEst,
        secuencia_inicial: getSecuencialActual(p.tipo_documento, nuevoEstablecimiento, primerPuntoDelEst),
      };
    });
  };

  const handleSecPuntoChange = (value) => {
    setSecForm((p) => ({
      ...p,
      punto_emision: normalizeCode(value),
      secuencia_inicial: getSecuencialActual(p.tipo_documento, p.establecimiento, value),
    }));
  };

  return (
    <>
      <TableRow>
        <TableCell sx={tdLabelSx}>
          Establecimiento y punto de emisión
          <Box sx={noteSx}>
            Administra los puntos desde donde se emitirán los documentos electrónicos.
          </Box>
        </TableCell>

        <TableCell sx={tdFieldSx}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openPuntoModal}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Añadir punto
              </Button>
            </Box>

            {puntosAgrupados.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "#fafafa",
                  borderStyle: "dashed",
                }}
              >
                <Typography sx={{ color: "#666", fontSize: 14 }}>
                  No hay puntos de emisión creados.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                {puntosAgrupados.map((grupo) => (
                  <Paper
                    key={grupo.establecimiento}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <StorefrontOutlinedIcon fontSize="small" color="primary" />
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: "#222", lineHeight: 1.1 }}>
                          Establecimiento {grupo.establecimiento}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#666" }}>
                          {grupo.nombre}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {grupo.puntos.map((p) => (
                        <Box
                          key={`${p.establecimiento}-${p.punto_emision}`}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.25,
                            py: 0.75,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1.5,
                            bgcolor: "#f8fafc",
                          }}
                        >
                          <Typography sx={{ fontWeight: 800 }}>
                            P. Emisión {p.punto_emision}
                          </Typography>
                          <Tooltip title="Eliminar punto">
                            <IconButton size="small" onClick={() => deletePunto(p)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={tdLabelSx}>
          Secuencia inicial de documentos electrónicos
          <Box sx={noteSx}>
            Configura la numeración por tipo de documento, establecimiento y punto de emisión.
          </Box>
        </TableCell>

        <TableCell sx={tdFieldSx}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openSecuencialModal()}
                sx={{ textTransform: "none", fontWeight: 700 }}
                disabled={!puntosUnicos.length}
              >
                Añadir o modificar secuencia
              </Button>
            </Box>

            {!puntosUnicos.length ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "#fafafa",
                  borderStyle: "dashed",
                }}
              >
                <Typography sx={{ color: "#666", fontSize: 14 }}>
                  Primero crea un punto de emisión para configurar secuenciales.
                </Typography>
              </Paper>
            ) : secuencialesAgrupados.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "#fafafa",
                  borderStyle: "dashed",
                }}
              >
                <Typography sx={{ color: "#666", fontSize: 14 }}>
                  No hay secuenciales configurados.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 1.5 }}>
                {secuencialesAgrupados.map((grupo) => (
                  <Paper
                    key={grupo.key}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <ReceiptLongOutlinedIcon fontSize="small" color="primary" />
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: "#222", lineHeight: 1.1 }}>
                          {grupo.establecimiento_nombre}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#666" }}>
                          Est. {grupo.establecimiento} · P. Emisión {grupo.punto_emision}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 1 }} />

                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                      {grupo.secuenciales.map((s) => (
                        <Box
                          key={`${s.tipo_documento}-${s.establecimiento}-${s.punto_emision}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            px: 1.25,
                            py: 1,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1.5,
                            bgcolor: "#f8fafc",
                            minWidth: 0,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: "#111", lineHeight: 1.15 }}>
                              {getTipoShortLabel(s.tipo_documento)}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "#444", fontWeight: 700 }}>
                              {formatSecuencial(s.secuencia_inicial)}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                            <Tooltip title="Editar secuencia">
                              <IconButton size="small" onClick={() => openSecuencialModal(s)}>
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar secuencial">
                              <IconButton size="small" onClick={() => deleteSecuencial(s)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <Modal open={puntoOpen} onClose={() => setPuntoOpen(false)}>
        <Box sx={modalBoxSx}>
          <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: 18 }}>
            Crear punto de emisión
          </Typography>

          <Typography sx={{ color: "#666", fontSize: 13, mb: 2 }}>
            Selecciona el establecimiento y escribe el código del punto de emisión.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 2,
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Establecimiento</InputLabel>
              <Select
                label="Establecimiento"
                value={puntoForm.establecimiento}
                onChange={(e) =>
                  setPuntoForm((p) => ({
                    ...p,
                    establecimiento: normalizeCode(e.target.value),
                  }))
                }
              >
                {establecimientosUnicos.map((e) => (
                  <MenuItem key={e.codigo} value={e.codigo}>
                    {e.codigo} - {e.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Punto de emisión"
              value={puntoForm.punto_emision}
              placeholder="Ej: 001, 010"
              onChange={(e) =>
                setPuntoForm((p) => ({
                  ...p,
                  punto_emision: e.target.value.replace(/\D/g, "").slice(0, 3),
                }))
              }
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button onClick={() => setPuntoOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={addPunto}>
              Guardar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={secuencialOpen} onClose={() => setSecuencialOpen(false)}>
        <Box sx={modalBoxSx}>
          <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: 18 }}>
            Configurar secuencia
          </Typography>

          <Typography sx={{ color: "#666", fontSize: 13, mb: 2 }}>
            Define la próxima numeración para cada tipo de documento.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 2,
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Tipo de documento</InputLabel>
              <Select
                label="Tipo de documento"
                value={secForm.tipo_documento}
                onChange={(e) => handleSecTipoChange(e.target.value)}
              >
                {tipoDocumentoOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Secuencial inicial"
              value={secForm.secuencia_inicial}
              placeholder="000000001"
              onChange={(e) =>
                setSecForm((p) => ({
                  ...p,
                  secuencia_inicial: e.target.value.replace(/\D/g, "").slice(0, 9),
                }))
              }
            />

            <FormControl fullWidth>
              <InputLabel>Establecimiento</InputLabel>
              <Select
                label="Establecimiento"
                value={secForm.establecimiento}
                onChange={(e) => handleSecEstablecimientoChange(e.target.value)}
              >
                {establecimientosUnicos.map((e) => (
                  <MenuItem key={e.codigo} value={e.codigo}>
                    {e.codigo} - {e.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Punto de emisión</InputLabel>
              <Select
                label="Punto de emisión"
                value={secForm.punto_emision}
                onChange={(e) => handleSecPuntoChange(e.target.value)}
              >
                {puntosDelEstablecimiento.map((p) => (
                  <MenuItem key={`${p.establecimiento}-${p.punto_emision}`} value={p.punto_emision}>
                    {p.punto_emision}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: "#f8fafc",
              borderRadius: 2,
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#555", fontWeight: 700 }}>
              Vista previa
            </Typography>
            <Typography sx={{ fontSize: 15, color: "#111", fontWeight: 800, mt: 0.5 }}>
              {getTipoLabel(secForm.tipo_documento)} · {normalizeCode(secForm.establecimiento)} -{" "}
              {normalizeCode(secForm.punto_emision)} - {formatSecuencial(secForm.secuencia_inicial)}
            </Typography>
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button onClick={() => setSecuencialOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={addSecuencial}>
              Guardar
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}